import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formDataToSend.append(key, value));

      const response = await fetch('https://readdy.ai/api/form/d3dhhirbr3iihosg1b90', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 bg-gray-950 border border-gray-700 focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 rounded-lg text-white placeholder-gray-500 text-sm outline-none transition-all duration-300';

  return (
    <section id="contact" className="py-24 bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/25 mb-5">
            <i className="ri-mail-send-line text-amber-400 mr-2 text-sm"></i>
            <span className="text-xs font-semibold text-amber-400 tracking-widest uppercase">Get In Touch</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Contact{' '}
            <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
              Us
            </span>
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            Have questions or want to connect with our corporate team? We&apos;re here to help.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Info */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-6">Contact Information</h3>

            <div className="space-y-5 mb-8">
              {[
                { icon: 'ri-mail-line', label: 'Email', value: 'info@villarrealgroup.com' },
                { icon: 'ri-map-pin-2-line', label: 'Location', value: 'Las Vegas, Nevada' },
                { icon: 'ri-time-line', label: 'Business Hours', value: 'Monday – Friday, 9am – 5pm' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-amber-500/10 border border-amber-500/20 rounded-lg shrink-0">
                    <i className={`${item.icon} text-lg text-amber-400`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">{item.label}</div>
                    <div className="text-gray-200 text-sm">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6">
              <h4 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
                <i className="ri-checkbox-multiple-line"></i>
                This form is reserved for:
              </h4>
              <ul className="space-y-2">
                {[
                  'Clients and patients seeking services',
                  'Authorized referral sources',
                  'Verified business matters',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                    <i className="ri-arrow-right-s-line text-amber-500"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* No Solicitation Policy */}
            <div className="border border-red-800/50 rounded-xl overflow-hidden">
              <div className="bg-red-900/60 px-4 py-3 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shield-keyhole-line text-red-300 text-base"></i>
                </div>
                <span className="text-red-200 font-bold text-xs tracking-widest uppercase">
                  Strict No Solicitation Policy
                </span>
              </div>
              <div className="bg-red-950/40 px-4 py-4 text-xs text-gray-400 space-y-2">
                <p>
                  This website <strong className="text-red-300">strictly prohibits</strong> unsolicited communications including sales, marketing, lead generation, or outreach of any kind.
                </p>
                <p>
                  Submissions not meeting the above criteria will be <strong className="text-red-300">ignored, filtered, and permanently blocked</strong>.
                </p>
                <p className="font-semibold text-red-400 text-center pt-1">
                  Proceed only if your inquiry is legitimate and relevant.
                </p>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form id="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                  Company / Organization
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="Your company name"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                  Subject *
                </label>
                <select
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="">Select a subject</option>
                  <option value="media">Media Inquiry</option>
                  <option value="general">General Question</option>
                  <option value="client">Client / Service Inquiry</option>
                  <option value="referral">Referral Source</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                  Message *
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`${inputClass} resize-vertical`}
                  placeholder="Tell us about your inquiry..."
                ></textarea>
                <p className="text-xs text-gray-600 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Send Message
                  </>
                )}
              </Button>

              {submitStatus === 'success' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-amber-300 text-sm flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  Thank you! Your message has been sent successfully.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-900/20 border border-red-700/40 rounded-lg p-4 text-red-300 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line text-lg"></i>
                  Sorry, there was an error. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
